import React, { useEffect, useState } from "react";
import { TextField, MenuItem } from "@mui/material";
import getApiBase from "../components/getApiBase";
import { Autocomplete } from "@mui/material";
import { animate, stagger } from "motion";

const CarDropdowns = ({ formData, handleChange }) => {
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);
  const [engineOptions, setEngineOptions] = useState([]);
  const [yearError, setYearError] = useState("");

  // ANIMATION
  useEffect(() => {
    const userInputs = Array.from(document.getElementsByClassName("userInput"));
    if (userInputs.length > 0) {
      animate(
        userInputs,
        { x: [-30, 0], opacity: [0, 1] },
        { delay: stagger(0.3) }
      );
    }
  }, []);

  useEffect(() => {
    const loadYears = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/cars/years`);
        const data = await res.json();
        //console.log("📅 Years loaded:", data);
        setYears(data || []);
      } catch (err) {
        console.error("❌ Failed to load years:", err);
      }
    };
    loadYears();
  }, []);

  useEffect(() => {
    if (!formData.carYear) return;

    const loadMakes = async () => {
      try {
        const res = await fetch(
          `${getApiBase()}/api/cars/makes?year=${formData.carYear}`
        );
        const data = await res.json();
        //console.log("🚗 Makes loaded:", data);
        setMakes(data || []);
      } catch (err) {
        console.error("❌ Failed to load makes:", err);
      }
    };
    loadMakes();
  }, [formData.carYear]);

  useEffect(() => {
    if (!formData.carYear || !formData.carMake) return;

    const loadModels = async () => {
      try {
        const res = await fetch(
          `${getApiBase()}/api/cars/models?year=${formData.carYear}&make=${
            formData.carMake
          }`
        );
        const data = await res.json();
        //console.log("🚘 Models loaded:", data);
        setModels(data || []);
      } catch (err) {
        console.error("❌ Failed to load models:", err);
      }
    };
    loadModels();
  }, [formData.carYear, formData.carMake]);

  useEffect(() => {
    if (!formData.carYear || !formData.carMake || !formData.carModel) return;

    const loadTrims = async () => {
      try {
        const res = await fetch(
          `${getApiBase()}/api/cars/trims?year=${formData.carYear}&make=${
            formData.carMake
          }&model=${formData.carModel}`
        );
        const data = await res.json();
        const trim = data || [];

        //console.log("🧩 Trims loaded:", trim);

        const uniqueTrims = [
          ...new Set(trim.map((t) => t.trim).filter(Boolean)),
        ];

        setTrims(uniqueTrims);
      } catch (err) {
        console.error("❌ Failed to load trims/engines:", err);
      }
    };
    loadTrims();
  }, [formData.carYear, formData.carMake, formData.carModel]);

  useEffect(() => {
    if (!formData.carYear || !formData.carMake || !formData.carModel) return;

    const loadEngines = async () => {
      try {
        const res = await fetch(
          `${getApiBase()}/api/cars/engine?year=${formData.carYear}&make=${
            formData.carMake
          }&model=${formData.carModel}&trim=${formData.carTrim}`
        );
        const data = await res.json();
        const engine_size = data || [];

        //console.log("🧩 Engine Sizes loaded:", engine_size);

        const engineSize = [
          ...new Set(engine_size.map((e) => e.cylinders).filter(Boolean)),
        ];

        //console.log("🧩 Unique Engine Sizes:", engineSize);
        setEngineOptions(engineSize);
      } catch (err) {
        console.error("❌ Failed to load trims/engines:", err);
      }
    };
    loadEngines();
  }, [formData.carYear, formData.carMake, formData.carModel]);

  return (
    <>
      <Autocomplete
        freeSolo
        disableClearable
        options={years.map((year) => year.toString())}
        value={formData.carYear}
        onChange={(event, newValue) => {
          // When user selects from dropdown or confirms a value
          if (newValue === null) {
            handleChange({ target: { name: "carYear", value: "" } });
          } else {
            const yearNum = parseInt(newValue);
            // Only accept if it's in the valid years list
            if (years.includes(yearNum)) {
              handleChange({ target: { name: "carYear", value: newValue.toString() } });
            } else {
              // Invalid year - clear it
              handleChange({ target: { name: "carYear", value: "" } });
            }
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          // Allow typing, but validate on blur/selection
          if (reason === 'input') {
            // User is typing - allow it temporarily for better UX
            const yearNum = parseInt(newInputValue);
            const currentYear = new Date().getFullYear();
            const maxYear = currentYear + 1; // Allow up to 1 year in the future
            // Only allow numeric input that could be valid (4 digits, 1900 to current year + 1)
            if (newInputValue === "" || 
                (newInputValue.length <= 4 && /^\d*$/.test(newInputValue) && 
                 (newInputValue === "" || (yearNum >= 1900 && yearNum <= maxYear)))) {
              handleChange({ target: { name: "carYear", value: newInputValue } });
            }
          }
        }}
        onBlur={(event) => {
          // Validate on blur - ensure value is in valid years list and not more than 1 year in future
          const currentValue = formData.carYear;
          if (currentValue) {
            const yearNum = parseInt(currentValue);
            const currentYear = new Date().getFullYear();
            const maxYear = currentYear + 1;
            
            if (!years.includes(yearNum) || yearNum > maxYear) {
              // Invalid year - clear it and show error
              handleChange({ target: { name: "carYear", value: "" } });
              if (yearNum > maxYear) {
                setYearError(`Year cannot be more than ${maxYear} (1 year in the future).`);
              } else if (!years.includes(yearNum)) {
                setYearError("Please select a valid year from the list.");
              } else {
                setYearError("Please enter a valid car year.");
              }
              // Clear error after 5 seconds
              setTimeout(() => setYearError(""), 5000);
            } else {
              setYearError(""); // Clear error if valid
            }
          } else {
            setYearError(""); // Clear error if empty
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className="userInput"
            label="Year"
            name="carYear"
            required
            fullWidth
            error={!!yearError}
            helperText={yearError}
            sx={{ mt: 2 }}
            InputProps={{
              ...params.InputProps,
              type: "search",
            }}
          />
        )}
      />

      <TextField
        select
        className="userInput"
        label="Make"
        name="carMake"
        fullWidth
        required
        value={formData.carMake}
        onChange={handleChange}
        disabled={!formData.carYear}
        sx={{ mt: 2 }}
      >
        <MenuItem value="">Select Make</MenuItem>
        {makes.map((make) => (
          <MenuItem key={make.id} value={make.name}>
            {make.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        className="userInput"
        label="Model"
        name="carModel"
        fullWidth
        required
        value={formData.carModel}
        onChange={handleChange}
        disabled={!formData.carMake}
        sx={{ mt: 2 }}
      >
        <MenuItem value="">Select Model</MenuItem>
        {models.map((model) => (
          <MenuItem key={model.id} value={model.name}>
            {model.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        className="userInput"
        label="Trim"
        name="carTrim"
        fullWidth
        value={formData.carTrim}
        onChange={handleChange}
        disabled={!trims.length}
        sx={{ mt: 2 }}
      >
        <MenuItem value="">Select Trim</MenuItem>
        {trims.map((trim) => (
          <MenuItem key={trim.id} value={trim}>
            {trim}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        className="userInput"
        label="Engine Size"
        name="engineSize"
        fullWidth
        value={formData.engineSize}
        onChange={handleChange}
        disabled={!engineOptions.length}
        sx={{ mt: 2 }}
      >
        <MenuItem value="">Select Engine</MenuItem>
        {engineOptions.map((engine, index) => (
          <MenuItem key={index} value={engine}>
            {engine}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        className="userInput"
        label="Mileage"
        name="mileage"
        fullWidth
        value={formData.mileage}
        onChange={handleChange}
        sx={{ mt: 2 }}
      >
        <MenuItem value="">Select Mileage</MenuItem>
        <MenuItem value="0-25,000">0 – 25,000</MenuItem>
        <MenuItem value="25,001-50,000">25,001 – 50,000</MenuItem>
        <MenuItem value="50,001-75,000">50,001 – 75,000</MenuItem>
        <MenuItem value="75,001-100,000">75,001 – 100,000</MenuItem>
        <MenuItem value="100,001-150,000">100,001 – 150,000</MenuItem>
        <MenuItem value="150,001-200,000">150,001 – 200,000</MenuItem>
        <MenuItem value="200,000+">200,000+</MenuItem>
      </TextField>
    </>
  );
};

export default CarDropdowns;
